import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  CircularProgress,
  Grid2,
  Paper,
  Table,
  TableBody,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../../views/components/common/pages/AppTablePagenation";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import {
  OpsInwardDevBucket,
  OpsInwardDevCard,
  OpsInwardDevVendorRow,
} from "../../../../../../models/Dashboard/OpsTat/OpsInwardDeviation";
import { OPS_INWARD_DEV_VENDOR_PAGE_SIZE } from "../../../../../../models/Dashboard/OpsTat/OpsTatFilters";
import { OpsInwardDevMetricId } from "../../../../../../models/Dashboard/OpsTat/OpsTatDrilldown";
import { NewOperationsDashboardStore } from "../../../../stores/dashboards/NewOperationsDashboardStore";
import { trackOpsTatAction } from "../../../../../../analytics/opsTatTracking";
import {
  formatOpsTatCount,
  formatOpsTatPercent,
  formatOpsTatSignedDays,
} from "./opsTatFormat";
import OpsTatHowToRead from "./OpsTatHowToRead";

interface OpsInwardDeviationProps {
  store: NewOperationsDashboardStore;
}

const clickableSx = {
  cursor: "pointer",
  outline: "none" as const,
};

const OpsTatInlineState = ({ message }: { message: string }) => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="8rem"
      textAlign="center"
      border="1px dashed"
      borderColor="divider"
      borderRadius={2}
      px={2}
    >
      <CustomTypography
        variant="heading6"
        fontWeight={500}
        color={theme.palette.customColors.greyTextColor}
      >
        {message}
      </CustomTypography>
    </Box>
  );
};

const SectionSubhead = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mt: 3,
        mb: 1.5,
        flexWrap: "wrap",
      }}
    >
      <CustomTypography variant="heading6" color={customColors.navyText}>
        {title}
      </CustomTypography>
      <CustomTypography variant="caption" color={customColors.greyTextColor}>
        {subtitle}
      </CustomTypography>
      <Box sx={{ flex: 1, minWidth: 24, height: "1px", bgcolor: "divider" }} />
    </Box>
  );
};

const deviationCaption = (mean: number | null | undefined): string => {
  try {
    if (mean == null || !Number.isFinite(mean) || mean === 0) {
      return "days vs promise";
    }
    return mean > 0 ? "days late" : "days early";
  } catch (error) {
    return "days vs promise";
  }
};

const getBucketFill = (theme: Theme, label: string): string => {
  try {
    const customColors = theme.palette.customColors;
    const lower = label.toLowerCase();
    if (lower.includes("early")) {
      return customColors.purpleMain;
    }
    if (lower.includes("on-time")) {
      return customColors.successGreen;
    }
    if (lower.includes("1–3") || lower.includes("1-3")) {
      return theme.palette.goldenYellow.main;
    }
    return customColors.errorRed;
  } catch (error) {
    return theme.palette.customColors.greyTextColor;
  }
};

const DeviationHistogram = ({ buckets }: { buckets: OpsInwardDevBucket[] }) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;

  try {
    if (buckets.length === 0) {
      return null;
    }
    const maxCount = Math.max(...buckets.map((bucket) => bucket.count), 1);

    return (
      <Grid2 container spacing={1} columns={12}>
        {buckets.map((bucket) => {
          const heightPct =
            bucket.count > 0 ? (bucket.count / maxCount) * 100 : 0;
          return (
            <Grid2
              key={`${bucket.lo}-${bucket.hi ?? "overflow"}-${bucket.label}`}
              size={{ xs: 4, sm: 2 }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    height: 48,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: "60%",
                      height: `${heightPct}%`,
                      minHeight: bucket.count > 0 ? 4 : 0,
                      borderRadius: "2px 2px 0 0",
                      bgcolor: getBucketFill(theme, bucket.label),
                    }}
                  />
                </Box>
                <CustomTypography
                  variant="caption"
                  color={customColors.navyText}
                  sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
                >
                  {formatOpsTatCount(bucket.count)}
                </CustomTypography>
                <CustomTypography
                  variant="caption"
                  color={customColors.greyTextColor}
                  sx={{ display: "block", lineHeight: 1.2 }}
                >
                  {bucket.label}
                </CustomTypography>
              </Box>
            </Grid2>
          );
        })}
      </Grid2>
    );
  } catch (error) {
    return null;
  }
};

const DeviationCard = ({
  card,
  title,
  onOpen,
}: {
  card: OpsInwardDevCard;
  title: string;
  onOpen: () => void;
}) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;
  const mean = card.deviation?.mean ?? null;

  const onKeyDown = (event: React.KeyboardEvent) => {
    try {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen();
      }
    } catch (error) {
      // ignore keyboard handler failures
    }
  };

  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      sx={{
        p: 2,
        borderRadius: 2,
        borderColor: "divider",
        height: "100%",
        ...clickableSx,
        "&:hover": {
          borderColor: customColors.purpleMain,
          bgcolor: theme.palette.action.hover,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <CustomTypography
          variant="caption"
          color={customColors.greyTextColor}
          sx={{ letterSpacing: "0.08em", textTransform: "uppercase" }}
        >
          {title}
        </CustomTypography>
        <CustomTypography
          variant="caption"
          color={customColors.greyTextColor}
          sx={{ ml: "auto", fontVariantNumeric: "tabular-nums" }}
        >
          n {formatOpsTatCount(card.n)}
        </CustomTypography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
        <CustomTypography
          variant="heading3"
          color={customColors.navyText}
          sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1 }}
        >
          {formatOpsTatSignedDays(mean)}
        </CustomTypography>
        <CustomTypography
          variant="caption"
          color={customColors.greyTextColor}
          sx={{ pb: 0.4 }}
        >
          {deviationCaption(mean)}
        </CustomTypography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          mt: 1.25,
        }}
      >
        <CustomTypography variant="caption" color={customColors.greyTextColor}>
          on-time{" "}
          <Box
            component="span"
            sx={{ color: customColors.navyText, fontWeight: 600 }}
          >
            {formatOpsTatPercent(card.on_time_pct)}
          </Box>
        </CustomTypography>
        {card.qc_adds_days != null ? (
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
          >
            QC adds{" "}
            <Box
              component="span"
              sx={{ color: customColors.navyText, fontWeight: 600 }}
            >
              {formatOpsTatSignedDays(card.qc_adds_days)}
            </Box>
          </CustomTypography>
        ) : (
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
          >
            median{" "}
            <Box
              component="span"
              sx={{ color: customColors.navyText, fontWeight: 600 }}
            >
              {formatOpsTatSignedDays(card.deviation?.p50)}
            </Box>
          </CustomTypography>
        )}
        <CustomTypography variant="caption" color={customColors.greyTextColor}>
          P90{" "}
          <Box
            component="span"
            sx={{ color: customColors.navyText, fontWeight: 600 }}
          >
            {formatOpsTatSignedDays(card.deviation?.p90)}
          </Box>
        </CustomTypography>
        {card.open_overdue != null ? (
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
          >
            open overdue{" "}
            <Box
              component="span"
              sx={{ color: customColors.navyText, fontWeight: 600 }}
            >
              {formatOpsTatCount(card.open_overdue)}
            </Box>
          </CustomTypography>
        ) : null}
      </Box>
    </Paper>
  );
};

const SignalCell = ({
  label,
  value,
  hint,
  onOpen,
}: {
  label: string;
  value: string;
  hint: string;
  onOpen: () => void;
}) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;

  const onKeyDown = (event: React.KeyboardEvent) => {
    try {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen();
      }
    } catch (error) {
      // ignore keyboard handler failures
    }
  };

  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor: "divider",
        height: "100%",
        ...clickableSx,
        "&:hover": {
          borderColor: customColors.purpleMain,
          bgcolor: theme.palette.action.hover,
        },
      }}
    >
      <CustomTypography
        variant="caption"
        color={customColors.greyTextColor}
        sx={{ letterSpacing: "0.08em", textTransform: "uppercase" }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="heading5"
        color={customColors.navyText}
        sx={{ mt: 0.5, fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </CustomTypography>
      <CustomTypography
        variant="caption"
        color={customColors.greyTextColor}
        sx={{ mt: 0.25, display: "block" }}
      >
        {hint}
      </CustomTypography>
    </Paper>
  );
};

const OpsInwardDeviation: React.FC<OpsInwardDeviationProps> = observer(
  ({ store }) => {
    const theme = useTheme();
    const customColors = theme.palette.customColors;
    const data = store.inwardSummaryResult?.data;
    const vendors = store.inwardVendorsResult?.data;
    const vendorRows = vendors?.rows ?? [];
    const [vendorPage, setVendorPage] = useState(1);

    useEffect(() => {
      try {
        setVendorPage(1);
      } catch (error) {
        console.error(error);
      }
    }, [store.inwardVendorsResult]);

    const openMetric = async (
      metricId: OpsInwardDevMetricId,
      options?: { vendorId?: number | null; cardTitle?: string },
    ) => {
      try {
        await store.openDrawer(metricId, {
          source: "inward_dev",
          vendorId: options?.vendorId,
          cardTitle: options?.cardTitle,
          cardSection: "inward_deviation",
        });
      } catch (error) {
        // drilldownError is set on the store
      }
    };

    const renderSignedVendorDays = (value: number | null) => {
      try {
        if (value == null || !Number.isFinite(value)) {
          return "—";
        }
        return formatOpsTatSignedDays(value);
      } catch (error) {
        return "—";
      }
    };

    const renderBody = () => {
      if (store.inwardError && !data) {
        return <OpsTatInlineState message={store.inwardError} />;
      }

      if (store.inwardLoading && !data) {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="8rem"
          >
            <CircularProgress size={28} />
          </Box>
        );
      }

      if (!data) {
        return (
          <OpsTatInlineState message="No inward deviation data for the current filters." />
        );
      }

      const fill = data.fill_rate;
      const fillPct = fill.fill_rate_pct;
      const barWidth =
        fillPct != null && Number.isFinite(fillPct)
          ? Math.max(0, Math.min(fillPct, 100))
          : 0;
      const distribution = data.section1_inward.distribution;
      const suggested = data.signals.suggested_use;
      const overdue = data.signals.open_overdue;
      const bias = data.signals.bias;
      const vendorColumns = vendors?.columns ?? [
        "Vendor",
        "Lines",
        "Raw dev p50",
        "Print dev p50",
        "On-time %",
      ];
      const vendorPageCount = Math.max(
        1,
        Math.ceil(vendorRows.length / OPS_INWARD_DEV_VENDOR_PAGE_SIZE),
      );
      const safeVendorPage = Math.min(Math.max(vendorPage, 1), vendorPageCount);
      const pagedVendorRows = vendorRows.slice(
        (safeVendorPage - 1) * OPS_INWARD_DEV_VENDOR_PAGE_SIZE,
        safeVendorPage * OPS_INWARD_DEV_VENDOR_PAGE_SIZE,
      );

      return (
        <>
          <Paper
            variant="outlined"
            role="button"
            tabIndex={0}
            onClick={() => {
              void openMetric("fill_rate", {
                cardTitle: "Promise fill rate",
              });
            }}
            onKeyDown={(event) => {
              try {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  void openMetric("fill_rate", {
                    cardTitle: "Promise fill rate",
                  });
                }
              } catch (error) {
                // ignore
              }
            }}
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: "divider",
              borderLeft: `4px solid ${theme.palette.goldenYellow.main}`,
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
              flexWrap: "wrap",
              ...clickableSx,
              "&:hover": {
                borderColor: customColors.purpleMain,
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <Box>
              <CustomTypography
                variant="caption"
                color={customColors.greyTextColor}
                sx={{ letterSpacing: "0.08em", textTransform: "uppercase" }}
              >
                Promise fill rate
              </CustomTypography>
              <CustomTypography
                variant="heading3"
                color={customColors.navyText}
                sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}
              >
                {formatOpsTatPercent(fill.fill_rate_pct)}
              </CustomTypography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <CustomTypography variant="body2" color={customColors.navyText}>
                {formatOpsTatCount(fill.with_raw_promise)} of{" "}
                {formatOpsTatCount(fill.ordered_products)} ordered products had
                an expected timeline recorded. Timelines were only introduced
                recently — everything below is measured on the{" "}
                {formatOpsTatCount(fill.with_raw_promise)}, and the{" "}
                {formatOpsTatCount(fill.blank)} blanks are excluded, not counted
                as on-time.
              </CustomTypography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 120,
                maxWidth: 260,
                height: 8,
                borderRadius: 4,
                bgcolor: customColors.veryLightGray,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${barWidth}%`,
                  height: "100%",
                  bgcolor: theme.palette.goldenYellow.main,
                }}
              />
            </Box>
          </Paper>

          <SectionSubhead
            title="1 · Against inward"
            subtitle="physical arrival vs promise (excludes QC)"
          />
          <Grid2 container spacing={2} columns={12}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <DeviationCard
                card={data.section1_inward.raw}
                title="Raw inward · mean deviation"
                onOpen={() => {
                  void openMetric("raw_inward_dev", {
                    cardTitle: "Raw inward · mean deviation",
                  });
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <DeviationCard
                card={data.section1_inward.print}
                title="Print inward · mean deviation"
                onOpen={() => {
                  void openMetric("print_inward_dev", {
                    cardTitle: "Print inward · mean deviation",
                  });
                }}
              />
            </Grid2>
          </Grid2>

          {distribution ? (
            <Paper
              variant="outlined"
              sx={{ mt: 2, p: 2, borderRadius: 2, borderColor: "divider" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <CustomTypography
                  variant="heading7"
                  color={customColors.navyText}
                >
                  Deviation spread — raw inward
                </CustomTypography>
                <CustomTypography
                  variant="caption"
                  color={customColors.greyTextColor}
                  sx={{ ml: "auto" }}
                >
                  {formatOpsTatCount(data.section1_inward.raw.n)} products with
                  a promise
                </CustomTypography>
              </Box>
              <DeviationHistogram buckets={distribution.buckets} />
            </Paper>
          ) : null}

          <SectionSubhead
            title="2 · Against inward + QC"
            subtitle="usable (QC-passed) material vs the same promise"
          />
          <Grid2 container spacing={2} columns={12}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <DeviationCard
                card={data.section2_inward_qc.raw}
                title="Raw inward + QC · mean deviation"
                onOpen={() => {
                  void openMetric("raw_qc_dev", {
                    cardTitle: "Raw inward + QC · mean deviation",
                  });
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <DeviationCard
                card={data.section2_inward_qc.print}
                title="Print inward + QC · mean deviation"
                onOpen={() => {
                  void openMetric("print_qc_dev", {
                    cardTitle: "Print inward + QC · mean deviation",
                  });
                }}
              />
            </Grid2>
          </Grid2>

          {/* <SectionSubhead
            title="Signals"
            subtitle="what the deviation is telling us"
          />
          <Grid2 container spacing={2} columns={12}>
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <SignalCell
                label="Optimism bias"
                value={formatOpsTatSignedDays(bias.mean_signed)}
                hint="mean signed deviation of section-1 raw"
                onOpen={() => {
                  void openMetric("bias");
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <SignalCell
                label="Open · past promise"
                value={formatOpsTatCount(overdue.total)}
                hint={`${formatOpsTatCount(overdue.raw)} raw · ${formatOpsTatCount(overdue.print)} print`}
                onOpen={() => {
                  void openMetric("open_overdue");
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <SignalCell
                label="Suggested-product use"
                value={formatOpsTatPercent(suggested.swap_rate_pct)}
                hint={`${formatOpsTatCount(suggested.swapped_lines)} swapped lines`}
                onOpen={() => {
                  void openMetric("suggested_use");
                }}
              />
            </Grid2>
          </Grid2> */}

          <Grid2 container spacing={2} columns={12} sx={{ mt: 2 }}>
            <Grid2 size={{ xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: "divider",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <CustomTypography
                    variant="heading7"
                    color={customColors.navyText}
                  >
                    Deviation by vendor
                  </CustomTypography>
                  <CustomTypography
                    variant="caption"
                    color={customColors.greyTextColor}
                    sx={{ ml: "auto" }}
                  >
                    promised-timeline reliability
                  </CustomTypography>
                </Box>
                {vendorRows.length === 0 ? (
                  <OpsTatInlineState message="No vendor deviation rows for the current filters." />
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {vendorColumns.map((column) => (
                            <AppTableHeaderCell
                              key={column}
                              sx={{
                                fontSize: 11,
                                whiteSpace: "nowrap",
                                textAlign:
                                  column === "Vendor" ? "left" : "right",
                              }}
                            >
                              {column}
                            </AppTableHeaderCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedVendorRows.map(
                          (row: OpsInwardDevVendorRow, index) => (
                            <TableRow
                              key={`${row.vendor_id ?? "none"}-${row.vendor}-${index}`}
                              hover
                              onClick={() => {
                                void openMetric("vendor_dev", {
                                  vendorId: row.vendor_id,
                                  cardTitle: row.vendor
                                    ? `Vendor · ${row.vendor}`
                                    : "Vendor deviation",
                                });
                              }}
                              sx={{
                                ...clickableSx,
                                "&:hover": {
                                  bgcolor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <AppTableRowCell sx={{ fontSize: 12 }}>
                                {row.vendor || "—"}
                              </AppTableRowCell>
                              <AppTableRowCell
                                sx={{ fontSize: 12, textAlign: "right" }}
                              >
                                {formatOpsTatCount(row.lines)}
                              </AppTableRowCell>
                              <AppTableRowCell
                                sx={{ fontSize: 12, textAlign: "right" }}
                              >
                                {renderSignedVendorDays(row.raw_dev_p50)}
                              </AppTableRowCell>
                              <AppTableRowCell
                                sx={{ fontSize: 12, textAlign: "right" }}
                              >
                                {renderSignedVendorDays(row.print_dev_p50)}
                              </AppTableRowCell>
                              <AppTableRowCell
                                sx={{ fontSize: 12, textAlign: "right" }}
                              >
                                {formatOpsTatPercent(row.on_time_pct)}
                              </AppTableRowCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                    {vendorRows.length > OPS_INWARD_DEV_VENDOR_PAGE_SIZE ? (
                      <AppTablePagenation
                        fontSize="12px"
                        fontColor={customColors.greyTextColor}
                        arrowButtonColor={customColors.navyText}
                        pageNo={safeVendorPage}
                        limit={OPS_INWARD_DEV_VENDOR_PAGE_SIZE}
                        totalCount={vendorRows.length}
                        showRowsPerPageOptions={false}
                        onPageChange={(_event, nextPage) => {
                          try {
                            setVendorPage(nextPage);
                            trackOpsTatAction(
                              "vendor_page_change",
                              store.appliedFilters,
                              {
                                page: nextPage,
                                pageSize: OPS_INWARD_DEV_VENDOR_PAGE_SIZE,
                                context: {
                                  page: nextPage,
                                  page_size: OPS_INWARD_DEV_VENDOR_PAGE_SIZE,
                                },
                              },
                            );
                          } catch (error) {
                            console.error(error);
                          }
                        }}
                        handleRowsChange={() => undefined}
                      />
                    ) : null}
                  </Box>
                )}
              </Paper>
            </Grid2>

            {/* <Grid2 size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                role="button"
                tabIndex={0}
                onClick={() => {
                  void openMetric("suggested_use");
                }}
                onKeyDown={(event) => {
                  try {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      void openMetric("suggested_use");
                    }
                  } catch (error) {
                    // ignore
                  }
                }}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: "divider",
                  height: "100%",
                  ...clickableSx,
                  "&:hover": {
                    borderColor: customColors.purpleMain,
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <CustomTypography
                    variant="heading7"
                    color={customColors.navyText}
                  >
                    Suggested vs original products
                  </CustomTypography>
                  <CustomTypography
                    variant="caption"
                    color={customColors.greyTextColor}
                    sx={{ ml: "auto" }}
                  >
                    is the fallback reliable?
                  </CustomTypography>
                </Box>
                <Grid2 container spacing={2} columns={12}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <CustomTypography
                      variant="caption"
                      color={customColors.greyTextColor}
                      sx={{
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      Original products
                    </CustomTypography>
                    <CustomTypography
                      variant="heading4"
                      color={customColors.navyText}
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {formatOpsTatSignedDays(suggested.original_dev_p50)}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color={customColors.greyTextColor}
                    >
                      median deviation
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <CustomTypography
                      variant="caption"
                      color={customColors.greyTextColor}
                      sx={{
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      Suggested swaps
                    </CustomTypography>
                    <CustomTypography
                      variant="heading4"
                      color={customColors.navyText}
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {formatOpsTatSignedDays(suggested.swap_dev_p50)}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color={customColors.greyTextColor}
                    >
                      median deviation ·{" "}
                      {formatOpsTatCount(suggested.swapped_lines)} lines
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2> */}
          </Grid2>
        </>
      );
    };

    return (
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          position: "relative",
        }}
      >
        {store.inwardLoading && data ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.45)",
              zIndex: 1,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : null}

        <CustomTypography variant="heading3" sx={{ mb: 0.5 }}>
          Inward Deviation — promise vs actual
        </CustomTypography>
        <CustomTypography
          variant="body2"
          color={customColors.greyTextColor}
          sx={{ mb: 1 }}
        >
          Sourcing records an expected raw-inward and print-inward timeline per
          product. This measures how actual inward landed against that promise —
          first on physical inward, then including QC. Deviation is actual −
          promised: negative = early, positive = late. Click any metric for its
          records.
        </CustomTypography>

        <OpsTatHowToRead
          section="inward"
          filters={store.appliedFilters}
          items={[
            "Signed days = actual − promised. + = late. − = early. Opposite sign from dispatch.",
            "Section 1 is physical inward versus the promise (no QC). Section 2 is QC-cleared versus the same promise.",
            "On-time means |deviation| ≤ 1 day. Blanks with no timeline are excluded, never counted as on-time.",
            "Include running folds overdue-open rows into the means as actual = now. Open-overdue counts do not change.",
          ]}
        />

        {renderBody()}
      </Paper>
    );
  },
);

export default OpsInwardDeviation;
